import type { StockTransfer, StockTransferStatus, Warehouse } from '@erp/shared';
import { isAxiosError } from 'axios';
import { ArrowLeft, ArrowLeftRight, Ban, PackageCheck, Pencil, Printer, Truck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { appUrl } from '../../lib/basePath.js';
import { formatDateTime } from '../../lib/intl.js';

interface Shortage { itemId: string; itemName: string | null; itemCode: string | null; requested: string; available: string }

const STATUS_TONE: Record<StockTransferStatus, 'success' | 'secondary' | 'danger' | 'default'> = {
  received: 'success',
  in_transit: 'default',
  draft: 'secondary',
  cancelled: 'danger',
};

/**
 * One transfer, and the two moments that actually move stock.
 *
 * Ship takes the quantity out of the source warehouse at that warehouse's own
 * average cost. Receive puts it into the destination at the shipped cost. They
 * are separate because the van is a real place: between the two the stock is on
 * nobody's shelf, and `in_transit` is the only honest way to say so.
 *
 * Receiving lets each line be corrected down to what actually arrived. The
 * shortfall is deliberately NOT written off as a second movement — the source
 * already lost the full shipped quantity, so the loss is visible as shipped vs
 * received on the line, where someone can see it and ask why.
 */
export function StockTransferDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation('inventory');
  const navigate = useNavigate();
  const canWrite = usePermission('stock.transfer.write');

  const [tr, setTr] = useState<StockTransfer | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  /** Lines the last Ship refused, with what is on the shelf against what the transfer asks. */
  const [shortages, setShortages] = useState<Shortage[]>([]);
  const [busy, setBusy] = useState(false);
  /** Line id → what actually arrived. Untouched lines accept the shipped qty. */
  const [received, setReceived] = useState<Record<string, string>>({});

  useEffect(() => {
    void api().get<StockTransfer>(`/stock-transfers/${id}`).then((r) => setTr(r.data)).catch(() => setTr(null));
    void api().get<{ items: Warehouse[] }>('/warehouses').then((r) => setWarehouses(r.data.items));
  }, [id]);

  const warehouseName = useMemo(() => {
    const by = new Map(warehouses.map((w) => [w.id, w.name]));
    return (wid: string): string => by.get(wid) ?? '—';
  }, [warehouses]);

  const act = async (verb: 'ship' | 'receive' | 'cancel', body?: unknown): Promise<void> => {
    setBusy(true);
    try {
      const r = await api().post<StockTransfer>(`/stock-transfers/${id}/${verb}`, body ?? {});
      setTr(r.data);
      setShortages([]);
      toast.success(t(`transfers.done.${verb}`, { defaultValue: 'Saved.' }));
    } catch (err) {
      // The server refuses for REASONS — say them. A blind "did not go through"
      // sent the owner hunting a bug when the source warehouse simply had zero
      // stock (the ship guard's insufficient_stock, which even names the item).
      const cause = isAxiosError(err)
        ? (err.response?.data as {
            cause?: { code?: string; itemId?: string; incoming?: string; existing?: string; shortages?: Shortage[] };
          } | undefined)?.cause
        : undefined;
      if (cause?.code === 'transfer_cost_no_rate') {
        toast.error(t('transfers.failed.noRate', {
          from: cause.incoming ?? '—',
          to: cause.existing ?? '—',
          defaultValue: 'No exchange rate between {{from}} and {{to}} — add one in Settings → Currencies, then receive again.',
        }));
      } else if (cause?.code === 'insufficient_stock') {
        const list = cause.shortages ?? [];
        setShortages(list);
        const first = list[0];
        toast.error(list.length > 0
          ? t('transfers.failed.shortToast', { from: tr ? warehouseName(tr.fromWarehouseId) : '—', count: list.length })
          : t('transfers.failed.insufficient', {
            item: first?.itemName ?? tr?.lines.find((l) => l.itemId === cause.itemId)?.itemName ?? '—',
            from: tr ? warehouseName(tr.fromWarehouseId) : '—',
            defaultValue: 'Not enough stock of "{{item}}" in {{from}} to ship this transfer.',
          }));
      } else if (cause?.code?.startsWith('invalid_status')) {
        toast.error(t('transfers.failed.status', { defaultValue: 'The transfer changed state — reload the page.' }));
      } else {
        toast.error(t(`transfers.failed.${verb}`, { defaultValue: 'That did not go through.' }));
      }
    } finally {
      setBusy(false);
    }
  };

  const ship = async (): Promise<void> => {
    if (!tr) return;
    const ok = await confirmDialog({
      title: t('transfers.shipTitle', { defaultValue: 'Ship this transfer?' }),
      message: t('transfers.shipConfirm', {
        defaultValue: 'This takes the stock out of {{from}} now. It will not be in {{to}} until the transfer is received.',
        from: warehouseName(tr.fromWarehouseId),
        to: warehouseName(tr.toWarehouseId),
      }),
      confirmLabel: t('transfers.ship', { defaultValue: 'Ship' }),
    });
    if (ok) await act('ship');
  };

  const receive = async (): Promise<void> => {
    if (!tr) return;
    // Only send the lines actually corrected — an untouched line means "all of
    // it arrived", and sending a value there would just be the same number.
    const overrides = tr.lines
      .filter((l) => received[l.id] !== undefined && received[l.id] !== '')
      .map((l) => ({ lineId: l.id, receivedQuantity: String(Number(received[l.id])) }));
    await act('receive', overrides.length > 0 ? { lines: overrides } : {});
  };

  const cancel = async (): Promise<void> => {
    const ok = await confirmDialog({
      title: t('transfers.cancelTitle', { defaultValue: 'Cancel this transfer?' }),
      message: t('transfers.cancelConfirm', { defaultValue: 'The draft is closed. No stock has moved, so nothing is returned.' }),
      tone: 'danger',
      confirmLabel: t('transfers.cancelIt', { defaultValue: 'Cancel transfer' }),
    });
    if (ok) await act('cancel');
  };

  if (!tr) return <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>;

  const isDraft = tr.status === 'draft';
  const inTransit = tr.status === 'in_transit';

  return (
    <div className="flex flex-col gap-4">
      <Link to="/inventory/transfers" className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden />
        {t('transfers.title', { defaultValue: 'Stock transfers' })}
      </Link>

      <Card className="flex flex-wrap items-start justify-between gap-4 p-5">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">{tr.transferNumber}</h1>
            <Badge variant={STATUS_TONE[tr.status]}>{t(`transfers.state.${tr.status}`, { defaultValue: tr.status })}</Badge>
          </div>
          <span className="flex items-center gap-2 text-sm text-fg-muted">
            {warehouseName(tr.fromWarehouseId)}
            <ArrowLeftRight className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden />
            {warehouseName(tr.toWarehouseId)}
          </span>
          <span className="text-xs text-fg-muted">
            {t('transfers.created', { defaultValue: 'Created' })}: {formatDateTime(tr.createdAt, { locale: i18n.language })}
            {tr.shippedAt ? ` · ${t('transfers.shippedAt', { defaultValue: 'Shipped' })}: ${formatDateTime(tr.shippedAt, { locale: i18n.language })}` : ''}
            {tr.receivedAt ? ` · ${t('transfers.receivedAt', { defaultValue: 'Received' })}: ${formatDateTime(tr.receivedAt, { locale: i18n.language })}` : ''}
          </span>
          {tr.notes ? <p className="mt-1 text-sm">{tr.notes}</p> : null}
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Printing is a read action — the person who loads the van is often
              not the one allowed to ship on the system, and they are exactly who
              needs the paper. A new tab, so the transfer stays open behind it. */}
          <Button variant="secondary" onClick={() => window.open(appUrl(`/inventory/transfers/${tr.id}/print`), '_blank')}>
            <Printer className="me-1.5 h-4 w-4" aria-hidden />
            {t('transfers.print.action', { defaultValue: 'Print / PDF' })}
          </Button>
          {canWrite && isDraft && (
            <>
              {/* Only a DRAFT is editable — once shipped the stock has already
                  left the shelf and the form redirects back here anyway. */}
              <Button variant="secondary" disabled={busy} onClick={() => navigate(`/inventory/transfers/${tr.id}/edit`)}>
                <Pencil className="me-1.5 h-4 w-4" aria-hidden />
                {t('transfers.edit', { defaultValue: 'Edit' })}
              </Button>
              <Button disabled={busy} onClick={() => void ship()}>
                <Truck className="me-1.5 h-4 w-4" aria-hidden />
                {t('transfers.ship', { defaultValue: 'Ship' })}
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => void cancel()}>
                <Ban className="me-1.5 h-4 w-4" aria-hidden />
                {t('transfers.cancelIt', { defaultValue: 'Cancel transfer' })}
              </Button>
            </>
          )}
          {canWrite && inTransit && (
            <Button disabled={busy} onClick={() => void receive()}>
              <PackageCheck className="me-1.5 h-4 w-4" aria-hidden />
              {t('transfers.receive', { defaultValue: 'Receive' })}
            </Button>
          )}
        </div>
      </Card>

      {shortages.length > 0 && tr && (
        <Card className="overflow-hidden border-danger p-0">
          <div className="border-b border-border p-4">
            <h2 className="text-sm font-semibold text-danger">{t('transfers.failed.shortTitle')} — {warehouseName(tr.fromWarehouseId)}</h2>
            <p className="mt-1 text-xs text-fg-muted">{t('transfers.failed.shortHint', { from: warehouseName(tr.fromWarehouseId) })}</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase text-fg-muted">
                <th className="p-3 text-start font-medium">{t('transfers.item', { defaultValue: 'Product' })}</th>
                <th className="p-3 text-end font-medium">{t('transfers.failed.shortHave')}</th>
                <th className="p-3 text-end font-medium">{t('transfers.failed.shortNeed')}</th>
                <th className="p-3 text-end font-medium">{t('transfers.failed.shortMissing')}</th>
              </tr>
            </thead>
            <tbody>
              {shortages.map((x) => (
                <tr key={x.itemId} className="border-b border-border/60 last:border-0">
                  <td className="p-3">
                    <div>{x.itemName ?? x.itemId.slice(0, 8)}</div>
                    {x.itemCode ? <div className="font-mono text-xs text-fg-muted" dir="ltr">{x.itemCode}</div> : null}
                  </td>
                  <td className="p-3 text-end font-mono">{Number(x.available)}</td>
                  <td className="p-3 text-end font-mono">{Number(x.requested)}</td>
                  <td className="p-3 text-end font-mono font-semibold text-danger">{Number(x.requested) - Number(x.available)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {inTransit && (
        <Card className="p-4 text-sm text-fg-muted">
          {t('transfers.inTransitHint', {
            defaultValue: 'This stock has left the source warehouse and is not yet in the destination. Correct any line below if less arrived, then receive.',
          })}
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase text-fg-muted">
              <th className="p-3 text-start font-medium">{t('transfers.item', { defaultValue: 'Product' })}</th>
              <th className="p-3 text-end font-medium">{t('transfers.qty', { defaultValue: 'Qty' })}</th>
              <th className="p-3 text-end font-medium">{t('transfers.shipped', { defaultValue: 'Shipped' })}</th>
              <th className="p-3 text-end font-medium">{t('transfers.receivedQty', { defaultValue: 'Received' })}</th>
            </tr>
          </thead>
          <tbody>
            {tr.lines.map((l) => {
              const short = l.shippedQuantity != null && l.receivedQuantity != null
                && Number(l.receivedQuantity) < Number(l.shippedQuantity);
              return (
                <tr key={l.id} className="border-b border-border/60 last:border-0">
                  <td className={`p-3 ${shortages.some((x) => x.itemId === l.itemId) ? 'text-danger' : ''}`}>
                    <div>{l.itemName ?? l.itemId.slice(0, 8)}</div>
                    {l.itemCode || l.itemBarcode ? (
                      <div className="font-mono text-xs text-fg-muted" dir="ltr">{[l.itemCode, l.itemBarcode].filter(Boolean).join(' · ')}</div>
                    ) : null}
                  </td>
                  <td className="p-3 text-end">{Number(l.quantity)}</td>
                  <td className="p-3 text-end">{l.shippedQuantity != null ? Number(l.shippedQuantity) : '—'}</td>
                  <td className="p-3 text-end">
                    {inTransit && canWrite ? (
                      <input
                        type="number"
                        min={0}
                        step="any"
                        inputMode="decimal"
                        value={received[l.id] ?? ''}
                        placeholder={l.shippedQuantity != null ? String(Number(l.shippedQuantity)) : ''}
                        aria-label={t('transfers.receivedQty', { defaultValue: 'Received' })}
                        onChange={(e) => setReceived((cur) => ({ ...cur, [l.id]: e.target.value }))}
                        className="h-9 w-24 rounded-md border border-border bg-bg px-2 text-end text-sm"
                      />
                    ) : (
                      <span className={short ? 'font-semibold text-warning' : undefined}>
                        {l.receivedQuantity != null ? Number(l.receivedQuantity) : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
